import { createActions, createReducer } from "reduxsauce"
import { takeLatest, put, call, select } from "redux-saga/effects"
import { filter, map, chain, find, keysIn, get, size, differenceWith, includes } from "lodash"
import pluralize from "pluralize"
import { Creators as notificationCreators } from "modules/notifications/model"
import { sendQuery } from "api/graphql"
import { post } from "api"
import { Creators as modelCreators } from "model/data"
import { getWatchlists } from "./selectors"
import { computeSecurities } from "helpers/watchlists"

export const { Types, Creators } = createActions({
  selectWatchlist: ["id", "watchlists"],
  addToWatchlists: ["watchlists", "securities", "watchlistData", "asOf"],
  toggleFromWatchlists: ["watchlists", "defaultWatchlists", "security", "asOf", "modelId"],
  addToWatchlistsSuccess: [],
  addToWatchlistsError: [],
  createWatchlistError: [],
  resetMenu: [],
  toggleFromWatchlistsSuccess: [],
  toggleFromWatchlistsError: [],
  searchWatchlists: ["searchValue"],
  toggleCreatingWatchlist: [],
  createWatchlist: ["name", "description", "securities", "asOf"],
}, {
  prefix: "ompnt/modules/watchlists-menu/",
})

const DEFAULT_STATE = {
  searchValue: "",
  creatingWatchlist: false,
  selectedWatchlists: null,
  updated: false,
}

function handleSearchWatchlists(state, { searchValue }) {
  return { ...state, searchValue }
}

function handleSelectWatchlist(state, { id, watchlists }) {
  let selectedWatchlists = null

  if (!state.selectedWatchlists) {
    if (watchlists) {
      selectedWatchlists = [...watchlists]
    } else {
      selectedWatchlists = []
    }
  } else {
    selectedWatchlists = [...state.selectedWatchlists]
  }

  if (selectedWatchlists.indexOf(id) >= 0) {
    selectedWatchlists = filter(selectedWatchlists, watchlist => watchlist !== id)
  } else {
    selectedWatchlists.push(id)
  }

  return { ...state, selectedWatchlists }
}

function handleAddToWatchlistsSuccess(state) {
  return { ...state, selectedWatchlists: null, searchValue: "", updated: true, creatingWatchlist: false }
}

function handleAddToWatchlistsError(state) {
  return { ...state }
}

function handleCreateWatchlistError(state) {
  return { ...state }
}

function handleResetMenu(state) {
  return { ...state, selectedWatchlists: null, searchValue: "", creatingWatchlist: false }
}

function handleToggleFromWatchlistsSuccess(state) {
  return { ...state, selectedWatchlists: null, searchValue: "", updated: true, creatingWatchlist: false }
}

function handleToggleFromWatchlistsError(state) {
  return { ...state }
}

function handleAddToWatchlists(state) {
  return { ...state, updated: false, creatingWatchlist: false }
}

function handleToggleFromWatchlists(state) {
  return { ...state, updated: false, creatingWatchlist: false }
}

function handleToggleCreatingWatchlist(state) {
  return { ...state, creatingWatchlist: !state.creatingWatchlist }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SELECT_WATCHLIST]: handleSelectWatchlist,
  [Types.ADD_TO_WATCHLISTS]: handleAddToWatchlists,
  [Types.ADD_TO_WATCHLISTS_SUCCESS]: handleAddToWatchlistsSuccess,
  [Types.ADD_TO_WATCHLISTS_ERROR]: handleAddToWatchlistsError,
  [Types.CREATE_WATCHLIST_ERROR]: handleCreateWatchlistError,
  [Types.TOGGLE_FROM_WATCHLISTS]: handleToggleFromWatchlists,
  [Types.TOGGLE_FROM_WATCHLISTS_SUCCESS]: handleToggleFromWatchlistsSuccess,
  [Types.TOGGLE_FROM_WATCHLISTS_ERROR]: handleToggleFromWatchlistsError,
  [Types.SEARCH_WATCHLISTS]: handleSearchWatchlists,
  [Types.TOGGLE_CREATING_WATCHLIST]: handleToggleCreatingWatchlist,
  [Types.RESET_MENU]: handleResetMenu,
})

function* addToWatchlists({ watchlists, securities, watchlistData, asOf }) {
  try {
    const { account, accountWatchlists } = yield select(state => ({
      account: state.global.selected.account,
      accountWatchlists: getWatchlists(state),
    }))

    let watchlistsToUpdate = null
    const numberOfSecurities = keysIn(securities).length

    if (watchlistData) {
      watchlistsToUpdate = watchlists.map((watchlist, i) => ({
        ...watchlistData,
        queryId: `watchlist${i}`,
      }))
    } else {
      watchlistsToUpdate = watchlists.map((watchlist, i) => ({
        ...find(accountWatchlists, { id: watchlist }),
        queryId: `watchlist${i}`,
      }))
    }

    const asOfField = (asOf && `,asOf:"${asOf}"`) || ""

    const watchlistsMutations = map(watchlistsToUpdate, watchlist => `
      ${watchlist.queryId}: addWatchlistSecurities(watchlistId: "${watchlist.id}", securities: $watchlistsSecurities ${asOfField}) {
         id
      }
    `)

    const { data, errors, apiMessages } = yield call(
      sendQuery,
      `mutation ($watchlistsSecurities: WatchlistSecuritiesInput!) {
        ${watchlistsMutations.join("")}
      }`,
      { watchlistsSecurities: computeSecurities(securities) },
      account,
    )

    if (!errors) {
      if (apiMessages.errors) {
        const successWatchlists = []
        const failedWatchlists = chain(data)
          .map((watchlist, queryId) => {
            if (!watchlist) {
              return find(watchlistsToUpdate, { queryId })
            } else {
              successWatchlists.push(find(watchlistsToUpdate, { queryId }))
              return null
            }
          })
          .filter(value => value)
          .value()

        yield put(notificationCreators.add("error", null, `An error happened while saving to the watchlist: ${failedWatchlists.map(watchlist => watchlist.name).join(", ")}.`))

        if (successWatchlists.length > 0) {
          yield put(Creators.addToWatchlistsSuccess())
          yield put(modelCreators.resetCollections(["accountWatchlists", "securityWatchlists", "watchlistSecurities"]))
          yield put(notificationCreators.add("success", null, `${pluralize("Security", numberOfSecurities)} added to your ${pluralize("watchlist", successWatchlists.length)}: ${successWatchlists.map(watchlist => watchlist.name).join(", ")}.`))
        } else {
          yield put(Creators.addToWatchlistsError())
        }
      } else {
        yield put(Creators.addToWatchlistsSuccess())
        yield put(modelCreators.resetCollections(["accountWatchlists", "securityWatchlists", "watchlistSecurities"]))
        yield put(notificationCreators.add("success", null, `${pluralize("Security", numberOfSecurities)} added to your ${pluralize("watchlist", watchlistsToUpdate.length)}.`))
      }
    } else {
      yield put(Creators.addToWatchlistsError())
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while adding securities to your watchlist."))
    }
  } catch {
    yield put(Creators.addToWatchlistsError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while adding securities to your watchlist."))
  }
}

function* toggleFromWatchlists({ watchlists, defaultWatchlists, security, asOf, modelId }) {
  try {
    const { account, accountWatchlists } = yield select(state => ({
      account: state.global.selected.account,
      accountWatchlists: getWatchlists(state),
    }))

    const toRemove = chain(defaultWatchlists)
      .filter(watchlist => watchlists.indexOf(watchlist) === -1)
      .map((watchlist, i) => ({
        ...find(accountWatchlists, { id: watchlist }),
        queryId: `watchlistRemove${i}`,
      }))
      .value()

    const toAdd = chain(watchlists)
      .filter(watchlist => defaultWatchlists.indexOf(watchlist) === -1)
      .map((watchlist, i) => ({
        ...find(accountWatchlists, { id: watchlist }),
        queryId: `watchlistAdd${i}`,
      }))
      .value()

    const asOfField = (asOf && `,asOf:"${asOf}"`) || ""

    const addMutations = map(toAdd, watchlist => `
      ${watchlist.queryId}: addWatchlistSecurities(watchlistId: "${watchlist.id}", securities: $watchlistsSecurities ${asOfField}) {
         id
      }
    `)

    const watchlistsSecurities = computeSecurities({
      [security.id]: {
        ticker: security.ticker,
        mic: security.mic || security.exchange,
        assetClass: security.assetClass,
        internalId: security.internalId,
      },
    })

    let data, errors, hasApiErrors
    if (size(addMutations)) {
      const result = yield call(
        sendQuery,
        `mutation ($watchlistsSecurities: WatchlistSecuritiesInput!) {
          ${addMutations.join("")}
        }`,
        {
          watchlistsSecurities,
        },
        account,
      )
      data = result.data
      errors = result.errors
      hasApiErrors = !!get(result, "apiMessages.errors")
    }

    let successRemoveFromWatchlists, failedRemoveWatchlists = []
    if (size(toRemove)) {
      const watchlistIds = map(toRemove, item => item.id)
      const { successWatchlistsIds } = yield call(post, `/api/${account}/watchlists/removeSecurities`, { modelId, date: asOf, securities: watchlistsSecurities, watchlistIds })
      failedRemoveWatchlists = differenceWith(toRemove, successWatchlistsIds, (arrVal, othVal) => arrVal.id === othVal)

      if (size(failedRemoveWatchlists)) {
        successRemoveFromWatchlists = filter(toRemove, item => includes(successWatchlistsIds, item.id))
        hasApiErrors = true
      }
    }

    if (!errors) {
      if (hasApiErrors) {
        const successAddToWatchlists = []
        const failedWatchlists = chain(data)
          .map((watchlist, queryId) => {
            const adding = find(toAdd, { queryId })
            if (!watchlist) {
              return adding
            }
            successAddToWatchlists.push(adding)
            return null
          })
          .filter(value => value)
          .concat(failedRemoveWatchlists)
          .value()

        yield put(notificationCreators.add("error", null, `An error happened while saving changes to the ${pluralize("watchlist", failedWatchlists.length)}: ${failedWatchlists.map(watchlist => watchlist.name).join(", ")}.`))

        if (successAddToWatchlists.length > 0 || successRemoveFromWatchlists.length > 0) {
          yield put(Creators.toggleFromWatchlistsSuccess(successAddToWatchlists, successRemoveFromWatchlists))
          yield put(modelCreators.resetCollections(["accountWatchlists", "securityWatchlists", "watchlistSecurities"]))

          if (successAddToWatchlists.length > 0) {
            yield put(notificationCreators.add("success", null, `Security added to your ${pluralize("watchlist", successAddToWatchlists.length)}: ${successAddToWatchlists.map(watchlist => watchlist.name).join(", ")}.`))
          }

          if (successRemoveFromWatchlists.length > 0) {
            yield put(notificationCreators.add("success", null, `Security removed from your ${pluralize("watchlist", successRemoveFromWatchlists.length)}: ${successRemoveFromWatchlists.map(watchlist => watchlist.name).join(", ")}.`))
          }
        } else {
          yield put(Creators.toggleFromWatchlistsError())
        }
      } else {
        yield put(Creators.toggleFromWatchlistsSuccess(toAdd, toRemove))
        yield put(modelCreators.resetCollections(["accountWatchlists", "securityWatchlists", "watchlistSecurities"]))

        if (toAdd.length > 0) {
          yield put(notificationCreators.add("success", null, `Security added to your ${pluralize("watchlist", toAdd.length)}: ${toAdd.map(watchlist => watchlist.name).join(", ")}.`))
        }

        if (toRemove.length > 0) {
          yield put(notificationCreators.add("success", null, `Security removed from your ${pluralize("watchlist", toRemove.length)}: ${toRemove.map(watchlist => watchlist.name).join(", ")}.`))
        }
      }
    } else {
      yield put(Creators.toggleFromWatchlistsError())
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while saving changes to your watchlist."))
    }
  } catch {
    yield put(Creators.toggleFromWatchlistsError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while saving changes to your watchlist."))
  }
}

function* startCreateWatchlist({ name, description, securities, asOf }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  const mutation = `mutation ($name: String!, $description: String) {
    createWatchlist (name: $name, description: $description) { name, description, id, alias, count, lastUpdated }
  }`

  try {
    const { data } = yield call(sendQuery, mutation, { name, description }, account)
    const result = get(data, "createWatchlist")

    if (result) {
      yield put(modelCreators.graphqlCreateSuccess("watchlists", "watchlists", "watchlists", result))
      yield put(modelCreators.graphqlCreateSuccess("accountWatchlists", "account-watchlists", "watchlists", result))
      yield put(modelCreators.resetCollections(["securityWatchlists"]))
      yield put(Creators.addToWatchlists([result.id], securities, result, asOf))
    } else {
      yield put(notificationCreators.add("error", null, "An error occurred while creating the watchlist."))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An error occurred while creating the watchlist."))
  }
}

export function* saga() {
  yield takeLatest(Types.ADD_TO_WATCHLISTS, addToWatchlists)
  yield takeLatest(Types.TOGGLE_FROM_WATCHLISTS, toggleFromWatchlists)
  yield takeLatest(Types.CREATE_WATCHLIST, startCreateWatchlist)
}
